import { useTranslation } from '@numistic/i18n'
import { cn, Input } from '@numistic/ui'
import { useId, useMemo, useRef, useState } from 'react'

import type { RefOption } from './ref-options.js'

const MAX_SHOWN = 50

/** Выбор из длинного справочника с поиском (ARIA combobox + listbox). */
export function RefCombobox({
  id,
  options,
  value,
  onChange,
  invalid,
  describedBy,
}: {
  id: string
  options: RefOption[]
  value: string | undefined | null
  onChange: (value: string | null) => void
  invalid?: boolean
  describedBy?: string
}) {
  const { t } = useTranslation()
  const listId = useId()
  const selected = options.find((o) => o.id === value)
  const [query, setQuery] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const blurTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const navigated = useRef(false)

  const matches = useMemo(() => {
    const q = (query ?? '').trim().toLocaleLowerCase('ru')
    const found = q ? options.filter((o) => o.label.toLocaleLowerCase('ru').includes(q)) : options
    return found.slice(0, MAX_SHOWN)
  }, [options, query])

  const choose = (option: RefOption) => {
    onChange(option.id)
    setQuery(null)
    setOpen(false)
  }
  // Список открывается с подсветкой текущего значения, а не первой строки.
  const openList = () => {
    if (open) return
    navigated.current = false
    setActive(
      Math.max(
        0,
        options.slice(0, MAX_SHOWN).findIndex((o) => o.id === value),
      ),
    )
    setOpen(true)
  }

  return (
    <div className="relative">
      <Input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        placeholder={t('common.search')}
        value={query ?? selected?.label ?? ''}
        onChange={(e) => {
          setQuery(e.target.value)
          setActive(0)
          setOpen(true)
          if (e.target.value === '') onChange(null)
        }}
        onFocus={openList}
        onClick={openList}
        onBlur={() => {
          blurTimer.current = setTimeout(() => {
            setOpen(false)
            setQuery(null)
          }, 150)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (!open) return openList()
            navigated.current = true
            setActive((i) => Math.min(i + 1, matches.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            navigated.current = true
            setActive((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter' && open) {
            e.preventDefault()
            // Без ввода и без стрелок Enter не меняет значение (просто закрывает список).
            const option = matches[active]
            if (option && (query !== null || navigated.current)) choose(option)
            else setOpen(false)
          } else if (e.key === 'Escape') {
            setOpen(false)
            setQuery(null)
          }
        }}
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-card py-1 text-sm shadow-md"
          onMouseDown={() => clearTimeout(blurTimer.current)}
        >
          {matches.length === 0 ? (
            <li className="px-3 py-1.5 text-muted-foreground">{t('catalog.empty')}</li>
          ) : null}
          {matches.map((o, i) => (
            <li
              key={o.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o.id === value}
              className={cn(
                'cursor-pointer px-3 py-1.5',
                i === active && 'bg-accent',
                o.id === value && 'font-medium',
              )}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(o)}
            >
              {o.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
